import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Leaf, MapPin, Share2, PiggyBank, CheckCircle,
  Mail, ArrowRight, Sparkles,
} from 'lucide-react';
import Seo from '../common/Seo';

const CONTACT_MAIL = 'info@sportyreplay.de';

const BENEFITS = [
  { icon: CheckCircle, title: 'Komplett kostenlos', desc: 'Keine Gebühren, keine Provision, kein Abo – für den Verein und alle Mitglieder.' },
  { icon: Users, title: 'Eigene Vereinsseite', desc: 'Jeder Verein bekommt eine eigene Seite mit allen Angeboten – ideal zum Teilen.' },
  { icon: Share2, title: 'Schnell geteilt', desc: 'Ein Link genügt – rein in die WhatsApp- oder Elterngruppe, fertig.' },
  { icon: Leaf, title: 'Nachhaltig', desc: 'Gut erhaltene Ausrüstung bekommt ein zweites Leben statt im Schrank zu landen.' },
  { icon: MapPin, title: 'Regional & persönlich', desc: 'Kaufen und verkaufen innerhalb des Vereins – Abholung vor Ort, kein Versandstress.' },
  { icon: PiggyBank, title: 'Kein Aufwand für den Vorstand', desc: 'Ihr müsst nichts verwalten – die Mitglieder stellen ihre Artikel selbst ein.' },
];

const STEPS = [
  { n: '1', title: 'Verein anmelden', desc: 'Kostenlos registrieren und den Vereinsnamen angeben.' },
  { n: '2', title: 'Mitglieder einladen', desc: 'Den Vereins-Link in eure WhatsApp- oder Elterngruppe teilen.' },
  { n: '3', title: 'Artikel einstellen', desc: 'Mitglieder fotografieren ausgemusterte Trikots, Schuhe & Co. und stellen sie ein.' },
  { n: '4', title: 'Kaufen & abholen', desc: 'Interessenten schreiben direkt, vereinbaren einen Treffpunkt – erledigt.' },
];

const ForClubs = () => {
  return (
    <div className="max-w-5xl mx-auto">
      <Seo
        title="Für Vereine – die kostenlose Sportbörse für euren Verein | SportyReplay"
        description="Gebrauchte Sportausrüstung kaufen und verkaufen – direkt in eurem Verein. Kostenlos, nachhaltig und regional. Jetzt euren Verein auf SportyReplay bringen."
        path="/fuer-vereine"
      />

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 rounded-3xl p-8 sm:p-12 text-white overflow-hidden shadow-2xl">
        <div className="relative z-10 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1 text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4" /> Für Vereine
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-4">
            Die kostenlose Sportbörse für euren Verein
          </h1>
          <p className="text-white/90 text-lg leading-relaxed mb-6">
            Kinder wachsen – die Ausrüstung wächst nicht mit. Auf SportyReplay kaufen und verkaufen
            eure Mitglieder gebrauchte Trikots, Schuhe und Ausrüstung direkt untereinander.
            Regional, nachhaltig und ohne einen Cent Gebühren.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-white text-orange-600 font-bold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            >
              Verein kostenlos anmelden <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={`mailto:${CONTACT_MAIL}?subject=Unser%20Verein%20auf%20SportyReplay`}
              className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              <Mail className="w-5 h-5" /> Kontakt aufnehmen
            </a>
          </div>
        </div>
      </div>

      {/* Problem / Empathie */}
      <div className="text-center max-w-2xl mx-auto mt-14 mb-10">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3">
          Kennst du das aus eurem Verein?
        </h2>
        <p className="text-gray-600 leading-relaxed">
          Der Nachwuchs braucht ständig neue Ausrüstung, während gut erhaltene Sachen ungenutzt
          im Keller liegen. SportyReplay bringt beides zusammen – innerhalb eurer Vereinsgemeinschaft,
          wo man sich kennt und vertraut.
        </p>
      </div>

      {/* Vorteile */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
        {BENEFITS.map((b) => (
          <div key={b.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
              <b.icon className="w-6 h-6 text-orange-500" />
            </div>
            <h3 className="font-bold text-gray-800 mb-1.5">{b.title}</h3>
            <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
          </div>
        ))}
      </div>

      {/* So funktioniert's */}
      <div className="mb-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center mb-10">
          So einfach startet ihr
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s) => (
            <div key={s.n} className="relative bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold flex items-center justify-center mb-4">
                {s.n}
              </div>
              <h3 className="font-bold text-gray-800 mb-1.5">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Abschluss-CTA */}
      <div className="bg-gray-900 rounded-3xl p-8 sm:p-12 text-center text-white mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold mb-3">
          Bringt euren Verein auf SportyReplay
        </h2>
        <p className="text-gray-300 max-w-xl mx-auto mb-7 leading-relaxed">
          In wenigen Minuten eingerichtet. Wir helfen euch gerne beim Start – meldet euch einfach.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/register"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
          >
            Jetzt kostenlos anmelden <ArrowRight className="w-5 h-5" />
          </Link>
          <a
            href={`mailto:${CONTACT_MAIL}?subject=Unser%20Verein%20auf%20SportyReplay`}
            className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            <Mail className="w-5 h-5" /> Fragen? Schreibt uns
          </a>
        </div>
      </div>
    </div>
  );
};

export default ForClubs;
